import React, { useState, useCallback } from 'react';
import { Button, Dropdown, Modal, Form, Select, DatePicker, Space, App } from 'antd';
import { DownloadOutlined, FileTextOutlined, FileZipOutlined, FileExcelOutlined } from '@ant-design/icons';
import moment from 'moment';
import api from '../services/api';

const { RangePicker } = DatePicker;
const { Option } = Select;

const ExportLogsButton = ({ targetId, targetName, disabled = false, mode = 'single' }) => {
    const { message } = App.useApp();
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportForm] = Form.useForm();
    const [exportFormat, setExportFormat] = useState('csv');

    const handleExportCancel = useCallback(() => {
        setExportModalVisible(false);
        exportForm.resetFields();
    }, [exportForm]);

    const handleExport = useCallback(async (values) => {
        setExportLoading(true);

        try {
            const { dateRange, status, timeRange, format } = values;

            // Prepare export parameters
            const params = {};

            // Always include format
            params.format = format || 'csv';

            // Only include other parameters if they have valid values
            if (status && status !== 'all') {
                params.status = status;
            }

            if (timeRange && timeRange !== 'custom') {
                params.timeRange = timeRange;
            }

            // Handle custom date range
            if (timeRange === 'custom' && dateRange && dateRange.length === 2) {
                params.startDate = dateRange[0].toISOString();
                params.endDate = dateRange[1].toISOString();
            }

            if (mode === 'single' && targetId) {
                // Single target export - use fetch with proper file handling
                const queryString = new URLSearchParams(params).toString();
                const response = await api.get(`/targets/${targetId}/export?${queryString}`, {
                    responseType: 'blob'
                });

                // Get content type from response - now we get full response for blob requests
                let contentType;
                if (response.headers && response.headers['content-type']) {
                    contentType = response.headers['content-type'];
                } else {
                    // Fallback content types based on format
                    switch (format) {
                        case 'csv':
                            contentType = 'text/csv';
                            break;
                        case 'json':
                            contentType = 'application/json';
                            break;
                        case 'txt':
                            contentType = 'text/plain';
                            break;
                        default:
                            contentType = 'application/octet-stream';
                    }
                }

                // Create filename based on format
                const safeTargetName = (targetName || `target_${targetId || 'unknown'}`)
                    .toLowerCase()
                    .replaceAll(' ', '')
                    .replaceAll(/[^a-zA-Z0-9]/g, '_');
                const filename = `logs_${safeTargetName}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.${format}`;

                // Get filename from Content-Disposition header if available
                let downloadFilename = filename;
                const contentDisposition = response.headers['content-disposition'];
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        downloadFilename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }

                // Create blob and download
                const blob = new Blob([response.data], { type: contentType });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', downloadFilename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                message.success(`Logs exported as ${format.toUpperCase()}!`);
                handleExportCancel();
            } else if (mode === 'bulk') {
                // Bulk export (would need targetIds from parent component)
                const response = await api.post('/targets/export/bulk', {
                    targetIds: [], // This should be passed from parent
                    filters: params
                }, {
                    responseType: 'blob'
                });

                // Get content type from response - now we get full response for blob requests
                let contentType = 'application/zip'; // Default for bulk export
                if (response.headers && response.headers['content-type']) {
                    contentType = response.headers['content-type'];
                }

                // Get filename from Content-Disposition header if available
                let downloadFilename = `logs_export_${moment().format('YYYY-MM-DD_HH-mm-ss')}.zip`;
                const contentDisposition = response.headers['content-disposition'];
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        downloadFilename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }

                // Handle bulk export (ZIP file)
                const blob = new Blob([response.data], { type: contentType });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', downloadFilename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                message.success('Logs exported as ZIP!');
                handleExportCancel();
            }

        } catch (error) {
            console.error('Export error:', error);
            message.error('Failed to export logs: ' + (error.response?.data?.message || error.message));
        } finally {
            setExportLoading(false);
        }
    }, [mode, targetId, targetName, message, handleExportCancel]);

    const getExportIcon = (format) => {
        switch (format) {
            case 'csv':
                return <FileExcelOutlined />;
            case 'json':
                return <FileTextOutlined />;
            case 'txt':
                return <FileTextOutlined />;
            case 'zip':
                return <FileZipOutlined />;
            default:
                return <DownloadOutlined />;
        }
    };

    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Export as CSV',
            icon: <FileExcelOutlined />,
            onClick: () => {
                setExportFormat('csv');
                setExportModalVisible(true);
            }
        },
        {
            key: 'json',
            label: 'Export as JSON',
            icon: <FileTextOutlined />,
            onClick: () => {
                setExportFormat('json');
                setExportModalVisible(true);
            }
        },
        {
            key: 'txt',
            label: 'Export as Text',
            icon: <FileTextOutlined />,
            onClick: () => {
                setExportFormat('txt');
                setExportModalVisible(true);
            }
        }
    ];

    return (
        <>
            <Dropdown
                menu={{ items: exportMenuItems }}
                placement="bottomLeft"
                disabled={disabled}
            >
                <Button
                    icon={<DownloadOutlined />}
                    type="primary"
                    disabled={disabled}
                >
                    Export Logs
                </Button>
            </Dropdown>

            <Modal
                title={
                    <Space>
                        {getExportIcon(exportFormat)}
                        Export Logs - {targetName || 'Selected Targets'}
                    </Space>
                }
                open={exportModalVisible}
                onCancel={handleExportCancel}
                footer={null}
                width={600}
            >
                <Form
                    form={exportForm}
                    layout="vertical"
                    onFinish={handleExport}
                    initialValues={{
                        format: exportFormat,
                        timeRange: '24h',
                        status: 'all'
                    }}
                >
                    <Form.Item
                        label="Export Format"
                        name="format"
                        rules={[{ required: true, message: 'Please select export format' }]}
                    >
                        <Select>
                            <Option value="csv">CSV (Excel Compatible)</Option>
                            <Option value="json">JSON</Option>
                            <Option value="txt">Text (Human Readable)</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Time Range"
                        name="timeRange"
                        rules={[{ required: true, message: 'Please select time range' }]}
                    >
                        <Select onChange={(value) => {
                            if (value !== 'custom') {
                                exportForm.setFieldsValue({ dateRange: null });
                            }
                        }}>
                            <Option value="1h">Last 1 Hour</Option>
                            <Option value="24h">Last 24 Hours</Option>
                            <Option value="7d">Last 7 Days</Option>
                            <Option value="30d">Last 30 Days</Option>
                            <Option value="custom">Custom Range</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.timeRange !== currentValues.timeRange}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('timeRange') === 'custom' ? (
                                <Form.Item
                                    label="Custom Date Range"
                                    name="dateRange"
                                    rules={[{ required: true, message: 'Please select date range' }]}
                                >
                                    <RangePicker
                                        showTime
                                        format="YYYY-MM-DD HH:mm:ss"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    <Form.Item
                        label="Status Filter"
                        name="status"
                    >
                        <Select>
                            <Option value="all">All Status</Option>
                            <Option value="UP">UP Only</Option>
                            <Option value="DOWN">DOWN Only</Option>
                            <Option value="TIMEOUT">TIMEOUT Only</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleExportCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={exportLoading}
                                icon={getExportIcon(exportFormat)}
                            >
                                Export {exportFormat.toUpperCase()}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ExportLogsButton;
